import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, ApiError } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})
type FormValues = z.infer<typeof Schema>

interface LoginResponse {
  token: string
  user: { id: string; email: string; name: string | null }
  tenantId: string
  role: string
}

export function SignInPage() {
  const t = useT()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(Schema),
  })

  const login = useMutation({
    mutationFn: (values: FormValues) => apiClient.post<LoginResponse>('/auth/login', values),
    onSuccess: ({ token }) => {
      localStorage.setItem('discover.jwt', token)
      navigate('/')
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : 'Sign-in failed')
    },
  })

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold mb-1">{t('auth.signin.title')}</h1>
      <p className="text-sm opacity-70 mb-6">{t('auth.signin.subtitle')}</p>

      <form onSubmit={handleSubmit((v) => login.mutate(v))} className="flex flex-col gap-3">
        <Field label={t('auth.email')} error={errors.email?.message}>
          <input
            {...register('email')}
            type="email"
            autoComplete="email"
            autoFocus
            className="auth-input"
          />
        </Field>
        <Field label={t('auth.password')} error={errors.password?.message}>
          <input
            {...register('password')}
            type="password"
            autoComplete="current-password"
            className="auth-input"
          />
        </Field>

        {serverError && (
          <p className="text-sm" style={{ color: 'var(--color-critical)' }}>{serverError}</p>
        )}

        <button
          type="submit"
          disabled={login.isPending}
          className="mt-1 px-3 py-2 rounded font-medium text-sm"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {login.isPending ? t('common.loading') : t('auth.signin.cta')}
        </button>
      </form>

      <div className="mt-6 text-sm opacity-80 flex justify-between">
        <Link to="/auth/forgot" className="hover:underline">{t('auth.forgot.link')}</Link>
        <Link to="/auth/signup" className="hover:underline">{t('auth.signup.link')}</Link>
      </div>
    </AuthShell>
  )
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full grid place-items-center p-6">
      <div className="w-full max-w-md p-6 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 font-semibold">
            <span className="text-lg">ZI² Discover</span>
          </Link>
        </div>
        {children}
        <style>{`
          .auth-input {
            width: 100%;
            padding: 8px 12px;
            border-radius: 6px;
            border: 1px solid var(--color-border);
            background: transparent;
            color: var(--color-fg);
            font-size: 14px;
          }
          .auth-input:focus {
            outline: 2px solid var(--color-accent);
            outline-offset: 1px;
          }
        `}</style>
      </div>
    </div>
  )
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm opacity-80">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{error}</p>}
    </label>
  )
}
